import type { QuestionBank } from "./types.js";

// Transcribed from the uploaded scanner asset (dpdpriskscan.html)'s `questions` array,
// per the Phase 2 master prompt's Phase A/C: reuse the existing content, make it
// data-driven, don't invent new questions. Wording, penalties, and section references
// are preserved as-is — see ARCHITECTURE.md's Module 1 discovery section for the
// source review, including the one bug this transcription does NOT carry forward
// (the source's unescaped apostrophe that breaks JS parsing — moot here, this is a
// TypeScript string literal, not hand-rolled single-quoted JS).
export const dpdpV1: QuestionBank = {
  id: "dpdp",
  version: "1.0.0",
  framework: "DPDP Act 2023",
  questions: [
    {
      id: "company_info",
      type: "text",
      text: "What is your company name, size, and sector?",
      hint: 'This helps us tailor the risk assessment to your industry. Example: "Fintech, 25 employees"',
    },
    {
      id: "collects_data",
      type: "boolean",
      riskField: "qualification",
      text: "Do you collect personal data from Indian users?",
      hint: "Personal data includes: name, email, phone, Aadhaar, health records, financial data, location, IP address, cookies.",
    },
    {
      id: "has_dpo",
      type: "boolean",
      riskField: "critical",
      text: "Do you have a Data Protection Officer (DPO) appointed?",
      hint: "DPDP Section 10: Mandatory for Significant Data Fiduciaries. Recommended for all. Must have board reporting line.",
      penalty: "₹150 crore (SDF violation)",
      section: "Section 10",
    },
    {
      id: "consent_mechanism",
      type: "boolean",
      riskField: "critical",
      text: "Is your consent mechanism granular, withdrawable, and auditable?",
      hint: "DPDP Section 6: Consent must be free, specific, informed, unconditional, and accompanied by clear purpose. Must be withdrawable with same ease as giving.",
      penalty: "₹200 crore (invalid consent)",
      section: "Section 6",
    },
    {
      id: "breach_sop",
      type: "boolean",
      riskField: "critical",
      text: "Do you have a data breach notification SOP with 72-hour SLA?",
      hint: "DPDP Section 14: Data Fiduciary must notify Board of data breach within 72 hours. Must also inform affected Data Principals.",
      penalty: "₹200 crore (failure to notify)",
      section: "Section 14",
    },
    {
      id: "dpr_fulfillment",
      type: "boolean",
      riskField: "critical",
      text: "Can you fulfill Data Principal Rights requests within 30 days?",
      hint: "DPDP Section 13: Right to Access (DAR), Correction (DCR), Erasure (DER), Grievance Redressal. 30-day hard SLA. No exceptions.",
      penalty: "₹50 crore per violation",
      section: "Section 13",
    },
    {
      id: "cross_border",
      type: "boolean",
      riskField: "high",
      text: "Have you assessed cross-border data transfers for adequacy?",
      hint: "DPDP Section 16: Transfers to countries without adequacy decision require Board-approved safeguards (BCRs/SCCs).",
      penalty: "₹250 crore (inadequate safeguards)",
      section: "Section 16",
    },
    {
      id: "children_data",
      type: "boolean",
      riskField: "high",
      text: "Do you process children's data (<18) with verifiable parental consent?",
      hint: 'DPDP Section 9: Verifiable parental consent + age gating required. "I am over 18" checkbox is NOT sufficient.',
      penalty: "₹200 crore (children's data violation)",
      section: "Section 9",
    },
    {
      id: "dpa_vendors",
      type: "boolean",
      riskField: "high",
      text: "Do you have Data Processing Agreements (DPAs) with all third-party vendors?",
      hint: "AWS, Stripe, Mixpanel, Google Analytics, Mailchimp, Slack — every processor that touches personal data needs a DPA.",
      penalty: "₹250 crore (inadequate safeguards)",
      section: "Section 8(2)",
    },
    {
      id: "retention_policy",
      type: "boolean",
      riskField: "medium",
      text: "Do you have a data retention and deletion policy with automated enforcement?",
      hint: "DPDP Section 12: Personal data must not be retained beyond necessary purpose. Must have deletion workflows.",
      penalty: "₹50 crore",
      section: "Section 12",
    },
    {
      id: "privacy_notice",
      type: "boolean",
      riskField: "medium",
      text: "Is your privacy notice compliant with DPDP Section 6?",
      hint: "Must include: purpose of processing, data categories, rights of Data Principals, DPO contact, grievance mechanism, retention period.",
      penalty: "₹50 crore",
      section: "Section 6(4)",
    },
    {
      id: "sdf_classification",
      type: "boolean",
      riskField: "medium",
      text: "Have you classified yourself as a Significant Data Fiduciary (SDF)?",
      hint: "DPDP Section 2(i): Based on volume/sensitivity of data, risk to Data Principals, potential impact on sovereignty. Self-assessment required.",
      penalty: "₹150 crore (SDF non-compliance)",
      section: "Section 2(i) + 10",
    },
    {
      id: "dpia",
      type: "boolean",
      riskField: "medium",
      text: "Do you conduct Data Protection Impact Assessments (DPIA)?",
      hint: "DPDP Section 27: Mandatory for SDFs. Recommended for high-risk processing involving sensitive personal data.",
      penalty: "₹150 crore (SDF violation)",
      section: "Section 27",
    },
    {
      id: "incident_response",
      type: "boolean",
      riskField: "medium",
      text: "Do you have an incident response plan with evidence preservation?",
      hint: "Must include: detection, containment, eradication, recovery, notification timelines, evidence chain of custody for regulatory proceedings.",
      penalty: "₹200 crore (failure to notify)",
      section: "Section 14",
    },
    {
      id: "any_work",
      type: "boolean",
      riskField: "qualification",
      text: "Have you done ANY DPDP compliance work so far?",
      hint: "Even a basic privacy policy review, GDPR mapping, or legal consultation counts. Be honest — this is for your protection.",
    },
  ],
};
